import React, { useState } from 'react';
import { Modal, Form, Button, ProgressBar, OverlayTrigger, Tooltip, FormCheck, Alert } from 'react-bootstrap';
import { writeContract, getWalletClient, waitForTransactionReceipt } from '@wagmi/core';
import communitiesContractABI from '../../contracts/tgetherCommunities.json';
import { useNavigate } from 'react-router-dom';
import * as config from '../../config.tsx';

const communitiesContractAddress = process.env.REACT_APP_COMMUNITIES_CONTRACT_ADDRESS as `0x${string}`;

function CreateCommunityModal({ show, setShow }: { show: boolean; setShow: (show: boolean) => void }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  const totalSteps = showAdvanced ? 9 : 8;

  const [createFormData, setCreateFormData] = useState({
    communityName: '',
    minCredsToProposeVote: 0,
    minCredsToVote: 0,
    maxCredsCountedForVote: 0,
    minProposalVotes: 0,
    memberAccessContract: '0x0000000000000000000000000000000000000000',
    proposalTime: 0,
    proposalDelay: 0,
    isInviteOnly: false,
  });

  const navigate = useNavigate();

  const handleCreateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setCreateFormData((prevState) => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentStep < totalSteps) {
      setCurrentStep((prevStep) => prevStep + 1);
    }
  };

  const handlePrev = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentStep > 1) {
      setCurrentStep((prevStep) => prevStep - 1);
    }
  };

  const handleAdvancedChange = () => {
    setShowAdvanced(!showAdvanced);
  };

  const handleClose = () => {
    setShow(false);
  };

  const handleCreateCommunity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createFormData.communityName.trim()) {
      console.error('Community name is required');
      return;
    }

    try {
      setLoading(true);

      // Get the wallet client for sending the transaction
      const walletClient = await getWalletClient(config.config);
      if (!walletClient) {
        console.error('No wallet client available.');
        return;
      }

      // Write the contract using the writeContract function
      const tx = await writeContract(config.config, {
        address: communitiesContractAddress,
        abi: communitiesContractABI.abi,
        functionName: 'createCommunity',
        args: [
          createFormData.communityName,
          Number(createFormData.minCredsToProposeVote),
          Number(createFormData.minCredsToVote),
          Number(createFormData.maxCredsCountedForVote),
          Number(createFormData.minProposalVotes),
          createFormData.memberAccessContract,
          Number(createFormData.proposalTime),
          Number(createFormData.proposalDelay),
          createFormData.isInviteOnly,
        ],
      });

      // Wait for transaction receipt
      const receipt = await waitForTransactionReceipt(config.config, {
        hash: tx,
      });

      setShow(false);
      setShowSuccessAlert(true);
      navigate(`/search-results?type=Community&value=${createFormData.communityName} `);
      
    } catch (error) {
      console.error('Error creating community:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteOnlyChange = (isInviteOnly: boolean) => {
    setCreateFormData((prevState) => ({
      ...prevState,
      isInviteOnly,
    }));
  };

  const renderTooltip = (text: string) => <Tooltip id="button-tooltip">{text}</Tooltip>;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Form.Group>
            <Form.Label>
              Community Name
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("The name of your community. This must be unique.")}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              name="communityName"
              value={createFormData.communityName}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 2:
        return (
          <Form.Group>
            <Form.Label>
              Min Creds to Propose Vote
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("This value sets the number of creds a member needs to propose a vote.")}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="minCredsToProposeVote"
              value={createFormData.minCredsToProposeVote}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 3:
        return (
          <Form.Group>
            <Form.Label>
              Min Creds to Vote
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("This value sets the minimum number of creds needed to vote on a proposal.")}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="minCredsToVote"
              value={createFormData.minCredsToVote}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 4:
        return (
          <Form.Group>
            <Form.Label>
              Max Creds Counted For Vote
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("Sets the maximum number of creds counted per member vote. Set to 0 for complete democracy.")}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="maxCredsCountedForVote"
              value={createFormData.maxCredsCountedForVote}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 5:
        return (
          <Form.Group>
            <Form.Label>
              Min Proposal Votes
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("Sets the minimum number of votes needed for a proposal to pass.")}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="minProposalVotes"
              value={createFormData.minProposalVotes}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 6:
        return (
          <Form.Group>
            <Form.Label>
              Proposal Time (in seconds)
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("Sets the duration for which a proposal will be active. Set to 0 for indefinite time.")}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="proposalTime"
              value={createFormData.proposalTime}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 7:
        return (
          <Form.Group>
            <Form.Label>
              Proposal Delay (in seconds)
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("Sets a delay before the proposal vote period begins.")}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              name="proposalDelay"
              value={createFormData.proposalDelay}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      case 8:
        return (
          <Form.Group>
            <Form.Label>
              Is Invite Only
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip("Sets whether the community is invite-only.")}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <div className="mb-2">
              <Button
                variant={createFormData.isInviteOnly ? "info" : "outline-info"}
                className="mr-2"
                onClick={() => handleInviteOnlyChange(true)}>
                True
              </Button>
              <Button
                variant={!createFormData.isInviteOnly ? "info" : "outline-info"}
                onClick={() => handleInviteOnlyChange(false)}>
                False
              </Button>
            </div>
          </Form.Group>
        );
      case 9:
        return (
          <Form.Group>
            <Form.Label>
              Member Access Contract
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip(
                  "This is an advanced setting. It allows a community to set an external contract to manage member invitations and credentials."
                )}
              >
                <Button variant="secondary" className="ml-1 p-0" style={{ border: "none", background: "none" }}>
                  <i className="bi bi-patch-question-fill"></i>
                </Button>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="text"
              name="memberAccessContract"
              value={createFormData.memberAccessContract}
              onChange={handleCreateInputChange}
            />
          </Form.Group>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {showSuccessAlert && (
        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible>
          Creation successful!
        </Alert>
      )}
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Create a Community</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div>Loading... Please wait for the transaction to complete.</div>
          ) : (
            <>
              <div className="advanced-parameters-switch mb-3">
                <FormCheck type="switch" id="custom-switch" label="Advanced Parameters" onClick={handleAdvancedChange} />
              </div>
              <Form>
                {renderStep()}
                <ProgressBar now={(currentStep / totalSteps) * 100} className="my-3" />
                <div className="d-flex justify-content-between">
                  <Button variant="outline-secondary" onClick={handlePrev} disabled={currentStep === 1}>
                    Previous
                  </Button>
                  {currentStep < totalSteps ? (
                    <Button variant="info" onClick={handleNext}>
                      Next
                    </Button>
                  ) : (
                    <Button variant="info" onClick={handleCreateCommunity} disabled={loading}>
                      Create Community
                    </Button>
                  )}
                </div>
              </Form>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default CreateCommunityModal;